// src/pages/AdminPostList.js

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import './AdminPostList.css'; 

const AdminPostList = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // NOVO: Estado para controlar o filtro de status
    const [filterStatus, setFilterStatus] = useState('todos'); // 'todos', 'publicado', 'rascunho'
    const [filterCategory, setFilterCategory] = useState('todos'); // NOVO: Estado para filtro de categoria
    const [allCategories, setAllCategories] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // 1. Buscar posts, autores e categorias em paralelo para mais eficiência
                const [postsSnapshot, authorsSnapshot, categoriesSnapshot] = await Promise.all([
                    getDocs(query(collection(db, 'blog_posts'), orderBy('publishedDate', 'desc'))),
                    getDocs(collection(db, 'autores')),
                    getDocs(collection(db, 'blog_categorias'))
                ]);

                // 2. Criar mapas para consulta rápida (muito mais performático)
                const authorsMap = new Map(authorsSnapshot.docs.map(doc => [doc.id, doc.data().nomeCompleto]));
                const categoriesMap = new Map(categoriesSnapshot.docs.map(doc => [doc.id, doc.data().slug]));
                const categoriesData = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setAllCategories(categoriesData);
                // 3. Mapear e NORMALIZAR os dados dos posts
                const postsData = postsSnapshot.docs.map(doc => {
                    const data = doc.data();
                    
                    // Lógica para lidar com inconsistências de dados
                    const authorId = data.author || data.autorId;
                    const mainCategoryId = data.mainCategory || data.categoriaId;
                    
                    return {
                        id: doc.id,
                        // Usa o campo 'title' ou 'titulo'
                        title: data.title || data.titulo,
                        // Usa o campo 'coverImage' ou 'urlImagemDestaque'
                        coverImage: data.coverImage || data.urlImagemDestaque,
                        // Busca o nome do autor no mapa, ou usa o campo antigo 'autorNome'
                        authorName: authorsMap.get(authorId) || data.autorNome || 'Autor Desconhecido',
                        // Busca o slug da categoria no mapa, ou usa o campo antigo 'slugCategoria'
                        categorySlug: categoriesMap.get(mainCategoryId) || data.slugCategoria,
                        // Garante que a data seja um objeto válido
                        publishedDate: data.publishedDate?.toDate() || new Date(), // Usa a data de publicação ou a data atual como fallback
                        slug: data.slug,
                        status: data.status || 'rascunho', // Define 'rascunho' se o status não existir
                        mainCategoryId: mainCategoryId
                    };
                });
                
                setPosts(postsData);
                setError(null);
            } catch (err) {
                console.error("Erro ao buscar dados:", err);
                setError("Falha ao carregar os posts. Tente novamente mais tarde.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleCreatePost = () => {
        navigate('/admin/posts/novo'); 
    };

    const handleEditPost = (postId) => {
        navigate(`/admin/posts/editar/${postId}`);
    };

    if (loading) return <div className="loading-message">A carregar posts...</div>;
    if (error) return <div className="error-message">{error}</div>;

    // NOVO: Lógica para filtrar os posts com base no estado do filtro
    const filteredPosts = posts.filter(post => {
        const statusMatch = filterStatus === 'todos' || post.status === filterStatus;
        const categoryMatch = filterCategory === 'todos' || post.mainCategoryId === filterCategory;
        return statusMatch && categoryMatch;
    });

    return (
        <div className="admin-post-list-container">
            <div className="admin-post-list-header">
                <h1>Gerenciamento de Posts</h1>
                {/* --- INÍCIO DA ALTERAÇÃO --- */}
                {/* Adicionando o botão que chama a nova função */}
                <button onClick={handleCreatePost} className="create-post-button">
                    Criar novo post
                </button>
                {/* --- FIM DA ALTERAÇÃO --- */}
            </div>

            <div className="filter-controls">
                {/* Filtros de status (sem alterações) */}
                <button onClick={() => setFilterStatus('todos')} className={`filter-button ${filterStatus === 'todos' ? 'active' : ''}`}>Todos</button>
                <button onClick={() => setFilterStatus('publicado')} className={`filter-button ${filterStatus === 'publicado' ? 'active' : ''}`}>Publicados</button>
                <button onClick={() => setFilterStatus('rascunho')} className={`filter-button ${filterStatus === 'rascunho' ? 'active' : ''}`}>Rascunhos</button>
                
                {/* --- INÍCIO: NOVO DROPDOWN DE CATEGORIAS --- */}
                <select 
                    className="filter-select"
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                >
                    <option value="todos">Todas as categorias</option>
                    {allCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                </select>
                {/* --- FIM: NOVO DROPDOWN DE CATEGORIAS --- */}
            </div>
            <div className="table-wrapper">
                <table>
                    <thead>
                        <tr>
                            <th>Post</th>
                            <th>Status</th>
                            <th>Data de Publicação</th>
                            <th>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPosts.length > 0 ? (
                            filteredPosts.map(post => (
                                <tr key={post.id}>
                                    <td className="post-info-cell">
                                        <img 
                                            src={post.coverImage || 'https://placehold.co/100x70/EFEFEF/333?text=Sem+Imagem'} 
                                            alt={post.title} 
                                            className="post-thumbnail"
                                        />
                                        <div className="post-title-author">
                                            {/* O link agora é montado de forma segura */}
                                            <Link to={`/blog/categoria/${post.categorySlug}/${post.slug}`} target="_blank" className="post-title-link">
                                                {post.title}
                                            </Link>
                                            <span className="post-author">Autor: {post.authorName}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`status-badge status-${post.status}`}>
                                            {post.status}
                                        </span>
                                    </td>
                                    <td>
                                        {post.publishedDate.toLocaleDateString('pt-BR')}
                                    </td>
                                    <td className="actions-cell">
                                        <button onClick={() => handleEditPost(post.id)} className="action-button edit-button">Editar</button>
                                        <button className="action-button delete-button">Apagar</button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" className="no-posts-message">Nenhum post encontrado com este filtro.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminPostList;